import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { CORE_SOLUTIONS, AI_JOURNEY } from "../config/brand";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";
import { scrollToId } from "../components/Navbar";
import { Tilt, hoverOnly } from "../components/Motion";

const LABELS = {
  "life-system": ["Plan", "Organize", "Prioritize", "Reset"],
  "ai-side-hustle": ["Explore", "Create", "Learn", "Build"],
};

const LABEL_POS = [
  "-left-2 top-6 sm:-left-4",
  "-right-2 top-1/3 sm:-right-4",
  "-left-2 bottom-1/4 sm:-left-4",
  "-right-2 bottom-6 sm:-right-4",
];

const Panel = ({ s, flip }) => {
  const [active, setActive] = useState(false);
  const labels = LABELS[s.id];

  return (
    <article
      id={s.id}
      data-testid={`core-solution-${s.id}`}
      {...hoverOnly({
        onMouseEnter: () => setActive(true),
        onMouseLeave: () => setActive(false),
      })}
      onClick={() => setActive((v) => !v)}
      className={`grid scroll-mt-24 cursor-pointer grid-cols-1 items-center gap-10 rounded-[2rem] border p-7 shadow-[0_16px_50px_rgba(90,48,73,0.07)] transition-[border-color,box-shadow,background-color] duration-400 sm:p-10 lg:grid-cols-12 lg:gap-12 lg:p-14 ${
        active
          ? "border-rose/50 bg-warmwhite shadow-[0_28px_70px_rgba(90,48,73,0.14)]"
          : "border-mauve/35 bg-warmwhite"
      }`}
    >
      <FadeUp className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <div className="relative mx-auto max-w-[360px]">
          <div
            aria-hidden="true"
            className={`absolute -inset-4 rounded-[2rem] transition-all duration-500 ${
              active ? "rotate-3 bg-blush/60" : "rotate-2 bg-blushcream"
            }`}
          />
          <Tilt
            max={4}
            className={`relative overflow-hidden rounded-2xl border border-mauve/30 transition-[box-shadow,transform] duration-500 ${
              active
                ? "-translate-y-2 shadow-[0_36px_80px_rgba(90,48,73,0.3)]"
                : "shadow-[0_26px_60px_rgba(90,48,73,0.22)]"
            }`}
          >
            <img
              src={s.image}
              alt={s.alt}
              className="w-full object-cover"
              width={840}
              height={1087}
              loading="lazy"
            />
          </Tilt>
          <span className="absolute -left-3 -top-3 z-10 rounded-xl bg-plum px-3.5 py-2 font-serif text-lg italic text-ivory shadow-lg">
            {s.number}
          </span>
          <AnimatePresence>
            {active &&
              labels.map((l, i) => (
                <motion.span
                  key={l}
                  initial={{ opacity: 0, y: 12, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.92 }}
                  transition={{ duration: 0.3, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className={`absolute z-10 rounded-lg bg-plum px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ivory shadow-lg ${LABEL_POS[i]}`}
                >
                  {l}
                </motion.span>
              ))}
          </AnimatePresence>
        </div>
      </FadeUp>

      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <FadeUp>
          <Eyebrow>{s.tag}</Eyebrow>
        </FadeUp>
        <LineReveal
          as="h3"
          lines={[s.headline]}
          className="mt-4 font-serif text-3xl font-medium leading-[1.12] tracking-tight text-plum sm:text-4xl lg:text-[2.7rem]"
        />
        {s.subheadline && (
          <FadeUp delay={0.1}>
            <p className="mt-3 font-serif text-xl italic text-rose">{s.subheadline}</p>
          </FadeUp>
        )}
        <FadeUp delay={0.15} className="mt-5 max-w-xl space-y-4 text-base leading-relaxed text-plumcharcoal/75">
          {s.copy.split("\n\n").map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </FadeUp>
        <FadeUp delay={0.2}>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {s.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm font-semibold text-plum">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-lg bg-blushcream text-rose">
                  <Check size={12} strokeWidth={2.5} />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.25} className="mt-8">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              scrollToId(s.id === "life-system" ? "whats-inside" : "offer");
            }}
            data-testid={`cta-${s.id}`}
            data-analytics-event={s.event}
            className="group inline-flex items-center gap-2 rounded-xl border border-plum/30 px-6 py-4 text-sm font-bold uppercase tracking-[0.08em] text-plum transition-[background-color,border-color,color] duration-200 hover:border-plum hover:bg-plum hover:text-ivory"
          >
            {s.cta}
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </FadeUp>
      </div>
    </article>
  );
};

const AiJourney = () => {
  const [active, setActive] = useState(0);
  return (
    <FadeUp delay={0.1} className="mt-10">
      <div
        className="rounded-[2rem] border border-plum/20 bg-plum p-7 sm:p-10"
        data-testid="ai-journey"
      >
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
          Inside the guide — the path it walks you through
        </p>
        <div className="mt-6 flex flex-wrap gap-2.5" role="tablist" aria-label="AI side hustle path">
          {AI_JOURNEY.map((s, i) => (
            <button
              key={s.stage}
              type="button"
              role="tab"
              aria-selected={active === i}
              data-testid={`ai-journey-${s.stage.toLowerCase()}`}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              className={`rounded-xl border px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-[background-color,color,border-color,transform] duration-200 ${
                active === i
                  ? "-translate-y-0.5 border-gold/60 bg-gold/15 text-gold"
                  : "border-ivory/20 bg-ivory/[0.05] text-ivory/65 hover:text-ivory"
              }`}
            >
              {String(i + 1).padStart(2, "0")} · {s.stage}
            </button>
          ))}
        </div>
        <div className="mt-5 min-h-[3.5rem]">
          <AnimatePresence mode="wait">
            <motion.p
              key={active}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="max-w-2xl font-serif text-xl italic leading-snug text-ivory sm:text-2xl"
              data-testid="ai-journey-text"
            >
              {AI_JOURNEY[active].text}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </FadeUp>
  );
};

export const CoreSolutions = () => (
  <section className="bg-ivory" aria-label="The two core solutions" data-testid="core-solutions-section">
    <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:py-28">
      <div className="max-w-3xl">
        <FadeUp>
          <Eyebrow>Two core systems</Eyebrow>
        </FadeUp>
        <LineReveal
          as="h2"
          lines={["One to manage your life.", "One to grow beyond it."]}
          className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl"
        />
      </div>
      <div className="mt-14 space-y-10">
        {CORE_SOLUTIONS.map((s, i) => (
          <Panel key={s.id} s={s} flip={i % 2 === 1} />
        ))}
        <AiJourney />
      </div>
    </div>
  </section>
);
